import React, { Suspense, lazy } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ROUTES } from "../router/routes";
import { authService } from "../services/authService";
import { useIsMobile } from "../hooks/useIsMobile";

const ProjectDocumentViewPage = lazy(
  () => import("../pages/desktop/ProjectDocumentViewPage"),
);
const DocumentGenerationPage = lazy(
  () => import("../pages/desktop/DocumentGenerationPage"),
);
const MobileRouter = lazy(() => import("./MobileRouter"));
const AutomationsPage = lazy(
  () => import("../pages/desktop/settings/AutomationsPage"),
);
const AutomationSettingsPage = lazy(
  () => import("../pages/desktop/settings/AutomationSettingsPage"),
);
const WeeklyCalendarPage = lazy(
  () => import("../components/Calendar/WeeklyCalendarPage"),
);
const LoginPage = lazy(() => import("../pages/desktop/LoginPage"));
const MobileLoginPage = lazy(() => import("../pages/mobile/MobileLoginPage"));
const DashboardPage = lazy(() => import("../pages/desktop/DashboardPage"));
const LeadsPage = lazy(() => import("../pages/desktop/LeadsPage"));
const FileStoragePage = lazy(() => import("../pages/desktop/FileStoragePage"));
const MsgTemplatePage = lazy(() => import("../pages/desktop/MsgTemplatePage"));
const DocumentsPage = lazy(() => import("../pages/desktop/DocumentsPage"));
const ReportsPage = lazy(() => import("../pages/desktop/ReportsPage"));
const ExpensesPage = lazy(() => import("../pages/desktop/ExpensesPage"));
const TeamPage = lazy(() => import("../pages/desktop/TeamPage"));
const SettingsPage = lazy(() => import("../pages/desktop/SettingsHomePage"));
const UsersPage = lazy(() => import("../pages/desktop/UsersPage"));
const NotFoundPage = lazy(() => import("../pages/desktop/NotFoundPage"));
const CalendarPage2 = lazy(
  () => import("../components/Calendar/CalendarPage2"),
);
const ProjectsPage = lazy(() => import("../pages/desktop/ProjectsPage"));
const ProjectDetailsPage = lazy(
  () => import("../pages/desktop/ProjectDetailsPage"),
);
const ProjectEditPage = lazy(() => import("../pages/desktop/ProjectEditPage"));
const BookkeepingDocumentPage = lazy(
  () => import("../pages/desktop/BookkeepingDocumentPage"),
);
const ResourcesPage = lazy(
  () => import("../pages/desktop/settings/ResourcesPage"),
);
const TablesPage = lazy(() => import("../pages/desktop/settings/TablesPage"));
const AttachmentsPage = lazy(
  () => import("../pages/desktop/settings/AttachmentsPage"),
);
const MenusPage = lazy(() => import("../pages/desktop/settings/MenusPage"));
const MenuCategoriesPage = lazy(
  () => import("../pages/desktop/settings/MenuCategoriesPage"),
);
const DocumentSettingsPage = lazy(
  () => import("../pages/desktop/settings/DocumentSettingsPage"),
);
const ItemsPage = lazy(() => import("../pages/desktop/settings/ItemsPage"));
const InterfacesPage = lazy(
  () => import("../pages/desktop/settings/InterfacesPage"),
);
const CustomFieldPage = lazy(
  () => import("../pages/desktop/settings/CustomFieldPage"),
);
const BaseItemsPage = lazy(
  () => import("../pages/desktop/settings/BaseItemsPage"),
);
const BusinessDetailsPage = lazy(
  () => import("../pages/desktop/settings/BusinessDetailsPage"),
);
const UsersPermissions = lazy(
  () => import("../pages/desktop/settings/UsersPermissions"),
);
const TasksPage = lazy(() => import("../pages/desktop/TasksPage"));
const ContactsPage = lazy(() => import("../pages/desktop/ContactsPage"));
const InvitationPage = lazy(
  () => import("../components/LinksFromExtrenal/InvitationPage"),
);
const SignDocumentPage = lazy(
  () => import("../components/LinksFromExtrenal/SignDocumentPage"),
);
const LegalAppendicePage = lazy(
  () => import("../pages/desktop/LegalAppendicePage"),
);
const ProjectMenuPage = lazy(
  () => import("../pages/desktop/buttonsMenu/ProjectMenuPage"),
);
const SelectMenuPage = lazy(
  () => import("../components/LinksFromExtrenal/selectMenuPage"),
);
const ReportMain = lazy(() => import("../components/Report/reportMain"));
const OpenFormatReportPage = lazy(
  () => import("../pages/desktop/OpenFormatReportPage"),
);
const CommunicationPage = lazy(
  () => import("../pages/desktop/CommunicationPage"),
);
const CommunicationHomePage = lazy(
  () => import("../pages/desktop/CommunicationHomePage"),
);
const AdminSettings = lazy(
  () => import("../components/Desktop/AdminSettings/AdminSettings"),
);
const IsraelInvoiceAuthResponse = lazy(
  () => import("../components/BookkeepingDocument/IsraelInvoiceAuthResponse"),
);
const ContactCardPage = lazy(() => import("../pages/desktop/ContactCardPage"));

const RouteFallback: React.FC = () => <div />;

const HomeRedirect: React.FC = () => {
  return authService.isAuthenticated() ? (
    <Navigate to={ROUTES.DASHBOARD} replace />
  ) : (
    <Navigate to={ROUTES.LOGIN} replace />
  );
};

const protectedRoutes = [
  { path: ROUTES.PROJECT_DOCUMENT_VIEW, component: ProjectDocumentViewPage },
  { path: ROUTES.DOCUMENT_GENERATION, component: DocumentGenerationPage },
  { path: ROUTES.WEEKLY_CALENDAR, component: WeeklyCalendarPage },
  { path: ROUTES.WEEKLY_CALENDAR_PROJECT, component: WeeklyCalendarPage },
  {
    path: ROUTES.WEEKLY_CALENDAR_PROJECT_CONTACT,
    component: WeeklyCalendarPage,
  },
  { path: ROUTES.DASHBOARD, component: DashboardPage },
  { path: ROUTES.CALENDAR, component: CalendarPage2 },
  { path: ROUTES.LEADS, component: LeadsPage },
  { path: ROUTES.FILE_STORAGE, component: FileStoragePage },
  { path: ROUTES.MSG_TEMPLATES, component: MsgTemplatePage },
  { path: ROUTES.SETTINGS, component: SettingsPage },
  { path: ROUTES.DOCUMENTS, component: DocumentsPage },
  { path: ROUTES.REPORTS, component: ReportsPage },
  { path: ROUTES.EXPENSES, component: ExpensesPage },
  { path: ROUTES.TASKS, component: TasksPage },
  { path: ROUTES.CONTACTS, component: ContactsPage },
  { path: ROUTES.TEAM, component: TeamPage },
  { path: ROUTES.USERS, component: UsersPage },
  { path: ROUTES.PROJECT_EVENT, component: ProjectDetailsPage },
  // { path: ROUTES.PREVIEW_PDF, component: PreviewPdfPage },
  { path: ROUTES.PROJECTS, component: ProjectsPage },
  { path: ROUTES.PROJECT_DETAILS, component: ProjectDetailsPage },
  { path: ROUTES.PROJECT_EDIT, component: ProjectEditPage },
  //דוחות
  { path: ROUTES.REPORT_DATA, component: ReportMain },
  { path: ROUTES.OPEN_FORMAT_REPORT, component: OpenFormatReportPage },
  //נתיב ליצירת תפריט
  { path: ROUTES.PROJECT_MENU, component: ProjectMenuPage },
  // נתיבי מסמכי הנהלת חשבונות
  { path: ROUTES.BOOKKEEPING_DOCUMENTS, component: BookkeepingDocumentPage },
  { path: ROUTES.COMMUNICATION, component: CommunicationPage },
  { path: ROUTES.COMMUNICATION_HOME, component: CommunicationHomePage },
  //איש קשר
  { path: ROUTES.CONTACT_CARD, component: ContactCardPage },
  // נתיבי הגדרות
  { path: ROUTES.SETTINGS_RESOURCES, component: ResourcesPage },
  { path: ROUTES.SETTINGS_TABLES, component: TablesPage },
  { path: ROUTES.SETTINGS_ATTACHMENTS, component: AttachmentsPage },
  { path: ROUTES.SETTINGS_MENUS, component: MenusPage },
  { path: ROUTES.SETTINGS_MENUS_CATEGORIES, component: MenuCategoriesPage },
  { path: ROUTES.SETTINGS_DOCUMENTS, component: DocumentSettingsPage },
  { path: ROUTES.SETTINGS_ITEMS, component: ItemsPage },
  { path: ROUTES.SETTINGS_INTERFACES, component: InterfacesPage },
  { path: ROUTES.SETTINGS_CUSTOM_FIELD, component: CustomFieldPage },
  { path: ROUTES.SETTINGS_BASEITEMS, component: BaseItemsPage },
  { path: ROUTES.SETTINGS_BUSINESS_DETAILS, component: BusinessDetailsPage },
  { path: ROUTES.SETTINGS_ADMIN, component: AdminSettings },
  { path: ROUTES.SETTINGS_USERS_AND_PERMISSIONS, component: UsersPermissions },
  { path: ROUTES.SETTINGS_LEGAL_APPENDICES, component: LegalAppendicePage },
  { path: ROUTES.SETTINGS_AUTOMATIONS, component: AutomationsPage },
  {
    path: ROUTES.SETTINGS_AUTOMATION_SETTINGS,
    component: AutomationSettingsPage,
  },
];

const DesktopRouter: React.FC = () => {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path={ROUTES.HOME} element={<HomeRedirect />} />
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />

        {/* לינק חיצוני - ללא אימות */}
        <Route path={ROUTES.INVITATION_LINK} element={<InvitationPage />} />
        <Route
          path={ROUTES.SIGN_DOCUMENT_LINK}
          element={<SignDocumentPage />}
        />
        <Route path={ROUTES.MENU_SELECT} element={<SelectMenuPage />} />
        <Route
          path={ROUTES.ISRAEL_INVOICE_AUTH_RESPONSE}
          element={<IsraelInvoiceAuthResponse />}
        />

        {protectedRoutes.map(({ path, component: Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

/**
 * Router ראשי - מחליט בין Desktop ל-Mobile Router
 */
const AppRouterContent: React.FC = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const isAuthPage = location.pathname === ROUTES.LOGIN;

  // OAuth callback must work the same in popup/mobile/desktop
  if (location.pathname === ROUTES.ISRAEL_INVOICE_AUTH_RESPONSE) {
    return <IsraelInvoiceAuthResponse />;
  }

  // אם זה דף התחברות:
  // - Desktop: LoginPage
  // - Mobile: MobileLoginPage
  if (isAuthPage) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route
            path={ROUTES.LOGIN}
            element={isMobile ? <MobileLoginPage /> : <LoginPage />}
          />
          <Route path="*" element={<Navigate to={ROUTES.LOGIN} />} />
        </Routes>
      </Suspense>
    );
  }

  if (isMobile) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <MobileRouter />
      </Suspense>
    );
  }

  return <DesktopRouter />;
};

export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <AppRouterContent />
    </BrowserRouter>
  );
};
